<template>
  <div ref="container">
    <v-skeleton-loader v-if="!imageSrc" class="responsive" type="image" />

    <v-fade-transition>
      <img
        v-if="imageSrc"
        class="responsive d-block"
        :src="imageSrc"
        alt="PDF Preview"
      />
    </v-fade-transition>

    <canvas ref="canvas" style="display: none"></canvas>
  </div>
</template>

<script>
import * as pdfjsLib from "pdfjs-dist";
import { openDB } from "idb";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

let queue = [];
let rendering = false;

function runQueue() {
  if (rendering || !queue.length) return;

  rendering = true;

  const job = queue.shift();

  job().finally(() => {
    rendering = false;
    runQueue();
  });
}

function enqueue(job) {
  queue.push(job);
  runQueue();
}

export default {
  props: {
    url: String,
    id: String,
  },

  data() {
    return {
      imageSrc: null,
      observer: null,
      db: null,
    };
  },

  async mounted() {
    this.db = await openDB("pdf-cache-v1", 1, {
      upgrade(db) {
        db.createObjectStore("thumbnails");
      },
    });

    this.createObserver();
  },

  beforeDestroy() {
    this.observer?.disconnect();
    if (this.imageSrc) URL.revokeObjectURL(this.imageSrc);
  },

  methods: {
    createObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            enqueue(() => this.loadThumbnail());
            this.observer.disconnect();
          }
        },
        {
          rootMargin: "50px",
          threshold: 0.1,
        },
      );

      this.observer.observe(this.$refs.container);
    },

    async loadThumbnail() {
      const cached = await this.db.get("thumbnails", this.id);

      if (cached) {
        this.imageSrc = URL.createObjectURL(cached);
        return;
      }

      try {
        const pdf = await pdfjsLib.getDocument({
          url: this.url,
          disableStream: true,
          disableAutoFetch: true,
        }).promise;

        const page = await pdf.getPage(1);

        const canvas = this.$refs.canvas;
        const ctx = canvas.getContext("2d");

        const viewport = page.getViewport({ scale: 1 });

        const scale = 400 / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        await page.render({
          canvasContext: ctx,
          viewport: scaledViewport,
        }).promise;

        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/webp", 0.8),
        );

        if (blob) {
          await this.db.put("thumbnails", blob, this.id);
          this.imageSrc = URL.createObjectURL(blob);
        }

        page.cleanup();
        pdf.destroy();

        canvas.width = 0;
        canvas.height = 0;
      } catch (e) {
        console.error("Erro preview pdf:", e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.responsive {
  aspect-ratio: 16 / 9;
  width: 100%;
  object-fit: cover;
  object-position: top;
}
</style>
